import React, { useRef, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { FaCamera } from 'react-icons/fa';
import useLanguage from '../hooks/useLanguage';
import taskApi from '../services/taskApi';
import { formatCount } from '../utils/numberFormatter';
import { GUEST_ASSESSMENT_MESSAGE_KEYS } from '../utils/taskErrorCodes';
import { TASK_PHOTO_MAX_BYTES, TASK_PHOTO_MIME_TYPES, TASK_PHOTO_MAX_COUNT } from '../config/taskPhotoRules';

/**
 * "Would you host this guest again?" — the question a turnover clean asks when it is closed
 * (tasks/post-checkout-review-and-replies.md L5–L7). One component for the crew card, the ops
 * completion dialog and the edit modal, so the question reads and behaves the same everywhere.
 *
 * A bare choice (July D4): «نعم» and «لا» look identical until one is picked — no colour, no warning.
 * «لا» opens the reason chips, a note (compulsory for «أخرى») and the photo of the problem, uploaded
 * here as an `issue` photo through the normal task-photo endpoint.
 *
 * The server decides whether the question is asked and whether it is required
 * (`task.guestAssessmentPrompt`); this component only renders it. The parent shows any problem
 * message beside its own complete button.
 */

export const EMPTY_GUEST_ASSESSMENT = { answer: null, reasonCode: null, note: '' };

const NOTE_MAX_LENGTH = 500;  // guestAssessmentService.NOTE_MAX_LENGTH

/**
 * What stops this answer being sent — the backend's rule, checked before the round trip.
 * @returns {'required'|'reasonCode'|'note'|'photo'|null} An untouched question is only a problem
 *   when the server says it is required.
 */
export const guestAssessmentProblem = (value, prompt, photos = []) => {
  if (!prompt) return null;
  if (!value?.answer) return prompt.required ? 'required' : null;
  if (value.answer === 'yes') return null;
  if (!value.reasonCode) return 'reasonCode';
  if (value.reasonCode === 'other' && !value.note?.trim()) return 'note';
  if (!photos.some(p => p.type === 'issue')) return 'photo';
  return null;
};

/** The body to send with the close, or undefined when nothing was answered. */
export const guestAssessmentPayload = (value) => {
  if (!value?.answer) return undefined;
  if (value.answer === 'yes') return { answer: 'yes' };
  return { answer: 'no', reasonCode: value.reasonCode, note: value.note?.trim() || undefined };
};

/** Translation key for a problem found by `guestAssessmentProblem`. */
export const guestAssessmentProblemKey = (problem) => GUEST_ASSESSMENT_MESSAGE_KEYS[problem] || null;

// One chip style for both rows: neutral until picked, brand purple once picked — the same for «نعم»
// and «لا», so the screen never suggests which answer is the right one (D4).
const chipStyle = (active) => ({
  border: `1px solid ${active ? '#471396' : '#ced4da'}`,
  borderRadius: '22px',
  background: active ? '#471396' : 'transparent',
  color: active ? '#fff' : '#495057',
  fontWeight: active ? 600 : 400
});

const GuestAssessmentQuestion = ({ taskId, prompt, value, onChange, photos = [], onPhotoAdded, disabled = false }) => {
  const { t, isArabic, language } = useLanguage();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [photoError, setPhotoError] = useState(null);

  if (!prompt) return null;

  const set = (patch) => onChange({ ...value, ...patch });
  const isNo = value.answer === 'no';
  const issuePhotoCount = photos.filter(p => p.type === 'issue').length;
  // One slot is always kept for the first problem photo (the server allows it past the cap), so the
  // button only stops once the maximum is reached AND the problem photo is already there.
  const atPhotoLimit = photos.length >= TASK_PHOTO_MAX_COUNT && issuePhotoCount > 0;
  const questionId = `guest-assessment-${taskId}`;
  const reasonsId = `${questionId}-reasons`;
  const notePrompt = t(value.reasonCode === 'other'
    ? 'tasks.guestAssessment.noteRequiredPlaceholder'
    : 'tasks.guestAssessment.notePlaceholder');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPhotoError(null);
    if (!TASK_PHOTO_MIME_TYPES.includes(file.type)) { setPhotoError(t('tasks.photos.invalidType')); return; }
    if (file.size > TASK_PHOTO_MAX_BYTES) { setPhotoError(t('tasks.photos.tooLarge')); return; }
    if (atPhotoLimit) { setPhotoError(t('tasks.photos.maxReached')); return; }

    setUploading(true);
    try {
      const result = await taskApi.uploadTaskPhoto(taskId, file, 'issue');
      if (onPhotoAdded) onPhotoAdded(result.data.photo);
    } catch {
      setPhotoError(t('tasks.photos.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="mb-3 p-2 rounded"
      style={{ border: '1px solid #e3dcef', background: '#fff' }}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      <div id={questionId} className="fw-semibold mb-2" style={{ color: '#090040', fontSize: '0.95rem' }}>
        {t('tasks.guestAssessment.question')}
      </div>

      <div className="d-flex gap-2" role="group" aria-labelledby={questionId}>
        {['yes', 'no'].map(answer => (
          <button
            key={answer}
            type="button"
            className="touch-btn touch-btn-sm"
            aria-pressed={value.answer === answer}
            disabled={disabled}
            // «نعم» carries nothing more (L6) — picking it clears a reason left over from «لا».
            onClick={() => set(answer === 'yes' ? { answer, reasonCode: null, note: '' } : { answer })}
            style={{ flex: '1 1 0', fontSize: '1rem', ...chipStyle(value.answer === answer) }}
          >
            {t(`tasks.guestAssessment.${answer}`)}
          </button>
        ))}
      </div>

      {isNo && (
        <div className="mt-2">
          <div id={reasonsId} className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>
            {t('tasks.guestAssessment.reasonPrompt')}
          </div>
          <div className="d-flex flex-wrap gap-1" role="group" aria-labelledby={reasonsId}>
            {prompt.reasons.map(code => (
              <button
                key={code}
                type="button"
                className="touch-btn touch-btn-sm"
                aria-pressed={value.reasonCode === code}
                disabled={disabled}
                onClick={() => set({ reasonCode: code })}
                style={{ fontSize: '0.85rem', padding: '0.3rem 0.75rem', ...chipStyle(value.reasonCode === code) }}
              >
                {t(`tasks.guestAssessment.reasons.${code}`)}
              </button>
            ))}
          </div>

          <Form.Control
            as="textarea"
            rows={2}
            className="mt-2 touch-input"
            value={value.note}
            maxLength={NOTE_MAX_LENGTH}
            disabled={disabled}
            onChange={(e) => set({ note: e.target.value })}
            placeholder={notePrompt}
            aria-label={notePrompt}
            dir={isArabic ? 'rtl' : 'ltr'}
            style={{ fontSize: '0.9rem', textAlign: isArabic ? 'right' : 'left' }}
          />

          <div className="mt-2">
            <div className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>
              {t('tasks.guestAssessment.photoLabel')}
            </div>
            {issuePhotoCount > 0 && (
              <div className="small text-success mb-1">
                ✓ {t('tasks.guestAssessment.photoAttached')} ({formatCount(issuePhotoCount, language)})
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={TASK_PHOTO_MIME_TYPES.join(',')}
              style={{ display: 'none' }}
              onChange={handleFile}
              disabled={disabled || uploading}
            />
            <Button
              variant="outline-secondary"
              className="w-100 touch-btn d-flex align-items-center justify-content-center gap-2"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || uploading || atPhotoLimit}
            >
              {uploading
                ? <Spinner animation="border" size="sm" />
                : <><FaCamera /> {t('tasks.guestAssessment.addPhoto')}</>}
            </Button>
            {/* At the limit the button is disabled — say why. The problem photo is already there by then. */}
            {!photoError && atPhotoLimit && (
              <div className="small text-muted mt-1">{t('tasks.photos.maxReached')}</div>
            )}
            {photoError && <div className="small text-danger mt-1" role="alert">{photoError}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestAssessmentQuestion;
